// src/components/ceremony/ResultsCeremony.js
// ============================================================================
// Founder sequence step 6: all results in -> the win/loss screen with Crits.
//
// Ported from the approved design (Media/ShowdownDemo/assets/endscreen.html):
// podium (2nd / CHAMPION / 3rd), the rest of the field in two columns, Crits
// counting up, purple King Crit dice raining over the champion, DNF rows struck
// through. Where the demo hardcoded the GT7 classification, this reads the real
// standings.
//
// What replaces the dead post-event screen: today a spectator who stays after a
// race sees the same layout with a dead player and a red LIVE dot. Now they get
// the result.
//
// COPY DOCTRINE: Crits only, never a currency figure. `crits` reaches this
// component already branched on entryCurrency server-side (payoutReceived holds
// CENTS on cash events), so a cash event simply shows placements and no amount.
// Rumbles (evergreen 3.3) are cash too: the crowd line never carries a figure.
// ============================================================================

import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';

import { CEREMONY_COLORS as C } from '../../constants/ceremony';
import CeremonyCanvas, {
  CeremonyHeader, anim, IS_WEB, DIE_PURPLE_1, DIE_PURPLE_20, resolveSrc,
} from './CeremonyCanvas';
import SponsorStrip from './SponsorStrip';
import useCritsEnabled from '../../hooks/useCritsEnabled';

// --- timeline (ms from phase entry) ----------------------------------------
const T_ROWS = 700;
// Vertical budget for the two standings columns (design px), and the gap the
// column flexbox puts between rows.
const TABLE_H = 450;
const ROW_GAP = 10;
// Below this pitch a standings row stops being readable on a stream.
const MIN_ROW_PITCH = 30;
const ROW_STAG = 90;
const T_P3 = 2600;
const T_P2 = 3150;
const T_P1 = 3850;
const T_RAIN = 4300;
const COUNT_MS = 900;

// Deterministic scatter — a re-render must not reshuffle the rain mid-fall.
function mulberry(seed) {
  return function next() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const RAIN = (() => {
  const rnd = mulberry(42);
  return Array.from({ length: 26 }, (_, i) => {
    const size = 30 + rnd() * 34;
    return {
      src: i % 3 ? DIE_PURPLE_20 : DIE_PURPLE_1,
      size,
      x: 700 + rnd() * 520,
      rot: (rnd() - 0.5) * 1080,
      dur: 1400 + rnd() * 900,
      delay: T_RAIN + rnd() * 1200,
    };
  });
})();

/** Eased count-up so the Crit totals tick rather than snap. */
function countUp(target, elapsed, startAt) {
  if (!target) return 0;
  const p = Math.max(0, Math.min(1, (elapsed - startAt) / COUNT_MS));
  return Math.round(target * (1 - (1 - p) ** 3));
}

export default function ResultsCeremony({
  title,
  strapline,
  standings = [],
  entryCrits,
  netPoolCrits,
  resultsFinal,
  payingPositions,
  rumbleTotalCrits,
  rumbleCritsEach,
  rumbleCount,
  mode,
  phaseElapsedMs = 0,
  eventId,
  branding = null,
  state = null,
}) {
  const effectiveBranding = branding ?? state?.branding ?? null;
  // Crits off: no entry/pool footer (there is no Crit entry or pool).
  const critsEnabled = useCritsEnabled();
  // LEGACY Crit boost (a pre-3.3 Crit split): the crowd's share, rendered as a
  // SECOND value per row — never folded into the placement number, which
  // would make an even everyone-benefits boost look winner-weighted. Gated on
  // finalRank downstream: quitters and rage-quits get nothing (matching the
  // backend, which only pays ranked rows). Only trusted once the split is on
  // the books AND pays at least 1.
  const boost = resultsFinal && rumbleCritsEach > 0 ? rumbleCritsEach : null;
  const legacyBand = !!boost && rumbleTotalCrits > 0;
  // Rumbles (evergreen 3.3) are cash, credited to the players after the
  // results are final, so this screen never shows a figure or a per-finisher
  // share: it only says the crowd boosted the race. rumbleCount is
  // floodCount + rumbleCount server-side; rumbleTotalCrits covers an older
  // server that doesn't send the count.
  const crowdBoosted = resultsFinal && !boost && (rumbleCount > 0 || rumbleTotalCrits > 0);
  const crowdNoun = !mode || ['racing', 'showdown'].includes(String(mode).toLowerCase()) ? 'RACE' : 'EVENT';
  const podium = useMemo(() => {
    const byRank = (r) => standings.find((s) => s.finalRank === r) || null;
    return { first: byRank(1), second: byRank(2), third: byRank(3) };
  }, [standings]);

  const rest = useMemo(
    () => standings.filter((s) => !s.finalRank || s.finalRank > 3),
    [standings],
  );
  // The table starts at design-Y 566 and the footer sits at ~1030, so the two
  // columns get ~450px. At the approved 60px pitch that is 7 rows a side — a
  // 16-racer grid exactly. Anything bigger compresses; past the legibility
  // floor it gets truncated with an honest tail instead of silently running off
  // the bottom of the canvas.
  const { shown, hidden, rowH, dense } = useMemo(() => {
    const maxPerCol = Math.floor(TABLE_H / MIN_ROW_PITCH);
    const maxRows = maxPerCol * 2;
    const fits = Math.min(rest.length, maxRows);
    const perCol = Math.ceil(fits / 2) || 1;
    const pitch = Math.min(60, Math.max(MIN_ROW_PITCH, Math.floor(TABLE_H / perCol)));
    return {
      shown: rest.slice(0, fits),
      hidden: rest.length - fits,
      rowH: pitch - ROW_GAP,
      dense: pitch - ROW_GAP < 40,
    };
  }, [rest]);

  const half = Math.ceil(shown.length / 2);

  // Paying placements: prefer the tapered band count the system banked at
  // seeding (right even before payouts run); fall back to counting who actually
  // got paid. Counting alone read "TOP 0" for the whole provisional window.
  // Crit events only (entryCrits > 0): payingPositions comes from the band
  // shapes server-side even on a zero pot, so without this gate a Crits-off
  // event would claim placements earn something. Results are never paid.
  const critEvent = entryCrits > 0;
  const paidCount = standings.filter((s) => s.crits > 0).length;
  const payoutCount = payingPositions || paidCount;
  const subtitle = [
    strapline,
    standings.length ? `${standings.length} ENTERED` : null,
    !critEvent ? null
      : payoutCount > 1 ? `TOP ${payoutCount} EARN CRITS` : payoutCount === 1 ? 'WINNER TAKES THE CRITS' : null,
  ].filter(Boolean).join('  ·  ');

  return (
    <CeremonyCanvas backdrop="podium">
      <CeremonyHeader
        title={`${title || 'CRITCASH EVENT'} — FINAL STANDINGS`}
        subtitle={subtitle}
        logoWidth={190}
        top={14}
      />

      {/* Ranks can be on screen before payouts have run; say which it is
          instead of implying a Crit total that hasn't been credited. The same
          slot carries the crowd once results are final: the legacy Crit band
          only when a Crit split is on the books (it names every finisher's
          share; never "+0"), otherwise the money-free Rumble line. Nothing
          about the crowd before that: Rumbles on an event that is cancelled
          or never finishes are refunded. A non-Crit event names no money at
          all while provisional. */}
      {!resultsFinal && (
        <Text style={styles.provisional}>
          {critEvent ? 'PROVISIONAL — CRITS SETTLING' : 'PROVISIONAL · RESULTS PENDING'}
        </Text>
      )}
      {legacyBand && (
        <Text style={styles.rumbleBand}>
          <Text style={styles.rumbleBandGold}>RUMBLE</Text>
          {`  —  ${rumbleTotalCrits.toLocaleString('en-US')} CRITS FROM THE CROWD  —  `}
          <Text style={styles.rumbleBandPurple}>{`EVERY FINISHER +${boost}`}</Text>
        </Text>
      )}
      {crowdBoosted && (
        <Text style={styles.rumbleBand}>
          <Text style={styles.rumbleBandGold}>{`THE CROWD BOOSTED THIS ${crowdNoun}`}</Text>
        </Text>
      )}

      <View style={styles.podium} pointerEvents="none">
        <PodiumCard player={podium.second} medal="2ND PLACE" at={T_P2} elapsed={phaseElapsedMs} boost={boost} />
        <PodiumCard player={podium.first} medal="CHAMPION" at={T_P1} elapsed={phaseElapsedMs} boost={boost} first />
        <PodiumCard player={podium.third} medal="3RD PLACE" at={T_P3} elapsed={phaseElapsedMs} boost={boost} />
      </View>

      <View style={styles.table} pointerEvents="none">
        <View style={styles.col}>
          {shown.slice(0, half).map((r, i) => (
            <StandingRow key={r.uid || i} player={r} index={i} total={shown.length} elapsed={phaseElapsedMs} height={rowH} dense={dense} boost={boost} />
          ))}
        </View>
        <View style={styles.col}>
          {shown.slice(half).map((r, i) => (
            <StandingRow key={r.uid || i} player={r} index={half + i} total={shown.length} elapsed={phaseElapsedMs} height={rowH} dense={dense} boost={boost} />
          ))}
        </View>
      </View>

      {/* Purple dice rain over the champion. */}
      {IS_WEB && phaseElapsedMs >= T_RAIN && (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          {RAIN.map((d, i) => (
            <img
              key={i}
              src={resolveSrc(d.src)}
              alt=""
              className="cc-cer-die"
              style={{
                left: d.x,
                top: 180,
                width: d.size,
                height: d.size,
                animationDelay: `${Math.max(0, d.delay - T_RAIN)}ms`,
                animationDuration: `${d.dur}ms`,
                '--cc-cer-rot': `${d.rot}deg`,
              }}
            />
          ))}
        </View>
      )}

      {/* Say what was dropped rather than letting a 50-player field silently
          end at row 30. */}
      {hidden > 0 && (
        <Text style={styles.truncated}>
          {`+ ${hidden} MORE ON THE FULL STANDINGS`}
        </Text>
      )}

      <View style={[styles.footer, anim('fadeDown', { delay: 400, duration: 700 })]}>
        <Text style={styles.footerText}>
          {critsEnabled && entryCrits ? (
            <>
              {'ENTRY '}
              <Text style={styles.footerGold}>{`${entryCrits} CRITS`}</Text>
              {'   ·   POOL '}
              <Text style={styles.footerGold}>
                {`${(netPoolCrits || 0).toLocaleString('en-US')} CRITS`}
              </Text>
            </>
          ) : null}
          <Text style={styles.footerSite}>   critcash.com</Text>
        </Text>
      </View>

      <SponsorStrip eventId={eventId} bottom={86} label="EVENT PARTNER" branding={effectiveBranding} />
    </CeremonyCanvas>
  );
}

function PodiumCard({ player, medal, at, elapsed, first, boost }) {
  // No card at all for a rank nobody holds — a 2-player H2H should show two
  // cards, not two cards and a ghost of a third place that doesn't exist.
  if (!player) return null;
  if (elapsed < at) return <View style={[styles.card, first && styles.cardFirst, styles.cardHidden]} />;
  const crits = countUp(player.crits, elapsed, at + 250);
  // Podium cards are looked up by finalRank, so a boost always applies here —
  // but a paying band can stop above the podium (2-player events pay top 1),
  // so the row must render for a boost-only placer too.
  const share = boost && player.finalRank ? boost : null;

  return (
    <View
      style={[styles.card, first && styles.cardFirst, anim('fadeUp', { duration: 520, easing: 'cubic-bezier(.2,1.1,.3,1)' })]}
    >
      {first && <Text style={styles.crown}>{CROWN}</Text>}
      <Text style={[styles.medal, first && styles.medalFirst]}>{medal}</Text>
      <Text style={[styles.cardName, first && styles.cardNameFirst]} numberOfLines={1}>
        {player.ign || player.name}
      </Text>
      {player.crits || share ? (
        <View style={styles.critsRow}>
          <Image source={DIE_PURPLE_20} style={styles.critsDie} resizeMode="contain" />
          {player.crits ? (
            <Text style={[styles.critsAmt, first && styles.critsAmtFirst]}>
              {crits.toLocaleString('en-US')}
            </Text>
          ) : null}
          {share ? <Text style={styles.critsBoost}>{`+${share}`}</Text> : null}
          <Text style={styles.critsLbl}>CRITS</Text>
        </View>
      ) : null}
    </View>
  );
}

function StandingRow({ player, index, total, elapsed, height, dense, boost }) {
  // Cascade from the back of the field forward, like the approved screen.
  const delay = T_ROWS + (total - 1 - index) * ROW_STAG;
  if (elapsed < delay) return <View style={{ height }} />;
  const dnf = player.state === 'dnf';
  const crits = countUp(player.crits, elapsed, delay + 150);
  // Every FINISHER shares the crowd's boost — rank required, so DNF and
  // still-unranked rows show nothing extra, matching who the backend pays.
  const share = boost && player.finalRank ? boost : null;

  return (
    <View
      style={[styles.row, { height }, dnf && styles.rowDnf, anim('fadeIn', { duration: 360 })]}
    >
      <View style={[styles.rowPos, dnf && styles.rowPosDnf, dense && styles.rowPosDense]}>
        <Text style={[styles.rowPosText, dnf && styles.rowPosTextDnf, dense && styles.rowTextDense]}>
          {player.finalRank ? `P${player.finalRank}` : '--'}
        </Text>
      </View>
      <Text
        style={[styles.rowIgn, dnf && styles.rowIgnDnf, dense && styles.rowTextDense]}
        numberOfLines={1}
      >
        {player.ign || player.name}
      </Text>
      {dnf ? (
        <Text style={[styles.rowDnfText, dense && styles.rowTextDense]}>OUT</Text>
      ) : player.crits > 0 || share ? (
        <View style={styles.rowEarn}>
          {!dense && player.crits > 0 && (
            <Image source={DIE_PURPLE_20} style={styles.rowEarnDie} resizeMode="contain" />
          )}
          {player.crits > 0 ? (
            <Text style={[styles.rowEarnText, dense && styles.rowTextDense]}>
              {crits.toLocaleString('en-US')}
            </Text>
          ) : (
            <Text style={styles.rowZero}>—</Text>
          )}
          {share ? (
            <Text style={[styles.rowBoostText, dense && styles.rowTextDense]}>{`+${share}`}</Text>
          ) : null}
        </View>
      ) : (
        <Text style={styles.rowZero}>—</Text>
      )}
    </View>
  );
}

const CROWN = '👑';

const styles = StyleSheet.create({
  provisional: {
    position: 'absolute',
    top: 262,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 5,
    color: C.CYAN_SOFT,
  },
  // Shares the provisional line's slot (262-306 gap above the podium); the
  // two never render together — the band (legacy or crowd) requires
  // resultsFinal, and only one of the two bands renders.
  rumbleBand: {
    position: 'absolute',
    top: 262,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 4,
    color: '#b9b9b9',
  },
  rumbleBandGold: { color: C.GOLD, letterSpacing: 6 },
  rumbleBandPurple: { color: C.PURPLE },
  critsBoost: {
    fontSize: 26,
    fontWeight: '900',
    color: C.PURPLE,
    paddingBottom: 5,
    ...(IS_WEB ? { textShadow: '0 0 14px rgba(160,80,255,0.4)' } : {}),
  },
  podium: {
    position: 'absolute',
    top: 306,
    left: 0,
    right: 0,
    flexDirection: 'row',
    gap: 26,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  card: {
    width: 340,
    borderRadius: 14,
    backgroundColor: 'rgba(17,17,17,0.94)',
    borderWidth: 1,
    borderColor: '#2a2a2a',
    alignItems: 'center',
    paddingTop: 22,
    paddingHorizontal: 18,
    paddingBottom: 18,
    minHeight: 150,
  },
  cardHidden: { opacity: 0 },
  cardFirst: {
    width: 400,
    borderWidth: 2,
    borderColor: C.GOLD,
    paddingTop: 30,
    ...(IS_WEB
      ? { boxShadow: '0 0 46px rgba(252,208,0,0.28), inset 0 0 30px rgba(252,208,0,0.06)' }
      : {}),
  },
  crown: { position: 'absolute', top: -46, fontSize: 60 },
  medal: { fontSize: 15, fontWeight: '900', letterSpacing: 4, color: C.CYAN_SOFT },
  medalFirst: { color: C.GOLD },
  cardName: { marginTop: 8, fontSize: 30, fontWeight: '900', letterSpacing: 1, color: '#ffffff' },
  cardNameFirst: {
    fontSize: 40,
    color: C.GOLD,
    ...(IS_WEB ? { textShadow: '0 0 24px rgba(252,208,0,0.5)' } : {}),
  },
  critsRow: { marginTop: 12, flexDirection: 'row', alignItems: 'flex-end', gap: 10 },
  critsDie: { width: 40, height: 40 },
  critsAmt: {
    fontSize: 40,
    fontWeight: '900',
    color: C.PURPLE,
    ...(IS_WEB ? { textShadow: '0 0 22px rgba(160,80,255,0.5)' } : {}),
  },
  critsAmtFirst: { fontSize: 52 },
  critsLbl: { fontSize: 15, fontWeight: '800', color: C.DIM, letterSpacing: 2, paddingBottom: 7 },

  table: {
    position: 'absolute',
    top: 566,
    left: (1920 - 1240) / 2,
    width: 1240,
    flexDirection: 'row',
    gap: 24,
  },
  col: { flex: 1, gap: ROW_GAP },
  row: {
    borderRadius: 9,
    backgroundColor: 'rgba(17,17,17,0.92)',
    borderWidth: 1,
    borderColor: C.BORDER,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingLeft: 10,
    paddingRight: 16,
  },
  rowDnf: { borderColor: '#3a1414', backgroundColor: 'rgba(24,10,10,0.9)' },
  rowPos: {
    width: 48,
    height: 34,
    borderRadius: 7,
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333333',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowPosDnf: { borderColor: '#4a1a1a' },
  rowPosDense: { width: 40, height: 24 },
  rowTextDense: { fontSize: 14 },
  rowPosText: { fontSize: 18, fontWeight: '900', color: C.GOLD },
  rowPosTextDnf: { color: C.DNF },
  rowIgn: { flex: 1, fontSize: 20, fontWeight: '800', color: '#e0e0e0' },
  rowIgnDnf: { color: '#9a6a6a', textDecorationLine: 'line-through' },
  rowEarn: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowEarnDie: { width: 26, height: 26 },
  rowEarnText: { fontSize: 19, fontWeight: '900', color: C.PURPLE },
  rowBoostText: { fontSize: 17, fontWeight: '800', color: C.PURPLE, opacity: 0.85 },
  rowZero: { fontSize: 16, fontWeight: '700', color: '#555555' },
  rowDnfText: { fontSize: 16, fontWeight: '900', letterSpacing: 2, color: C.DNF },

  truncated: {
    position: 'absolute',
    bottom: 132,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 4,
    color: '#6a6a6a',
  },
  footer: { position: 'absolute', bottom: 30, left: 0, right: 0, alignItems: 'center' },
  footerText: { fontSize: 19, fontWeight: '800', letterSpacing: 4, color: '#9a9a9a' },
  footerGold: { color: C.GOLD },
  footerSite: { color: C.CYAN },
});
